import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import {
  AdminQueryKeys,
  getEffectivePolicy,
  upsertRoutingPolicy,
  type EffectivePolicyResponse,
  type GetEffectivePolicyParams,
  type UpsertRoutingPolicyRequest,
} from '../api';

/**
 * Reads the effective preparation policy chain for a (tenant, MIME, digital, [documentType]) scope.
 * Disabled until the caller supplies a non-empty tenantId + mime so the page doesn't fire a flood of
 * 400s while the operator types.
 */
export function useEffectivePolicy(
  params: Partial<GetEffectivePolicyParams>,
): UseQueryResult<EffectivePolicyResponse> {
  const ready = Boolean(params.tenantId && params.mime);
  return useQuery({
    queryKey: ready ? AdminQueryKeys.preparationEffective(params as GetEffectivePolicyParams) : ['admin', 'preparation', 'effective', 'disabled'],
    queryFn: ({ signal }) => getEffectivePolicy(params as GetEffectivePolicyParams, signal),
    enabled: ready,
    staleTime: 15_000,
  });
}

/** Upserts the tenant routing policy (CU governance + chain). Invalidates the effective-policy query. */
export function useUpsertRoutingPolicy() {
  const qc = useQueryClient();
  return useMutation<void, Error, { tenantId: string; request: UpsertRoutingPolicyRequest }>({
    mutationFn: ({ tenantId, request }) => upsertRoutingPolicy(tenantId, request),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'preparation'] });
    },
  });
}
